// Authentication hook

import React, { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { authApi } from '../utils/api';
import toast from 'react-hot-toast';
import type { User, UseAuthReturn, RegisterForm } from '../types';

interface AuthContextType extends UseAuthReturn {}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  // Initialize auth state on app load
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (token) {
          const response = await authApi.getMe();
          setUser(response.user);
        }
      } catch (error) {
        // Token might be expired or invalid
        localStorage.removeItem('auth_token');
        localStorage.removeItem('csrf_token');
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      const response = await authApi.login(email, password);
      
      setUser(response.user);
      
      // Store token if provided
      if (response.token) {
        localStorage.setItem('auth_token', response.token);
      }
      
      toast.success('Welcome back!');
    } catch (error: any) {
      const message = error.response?.data?.error || error.message || 'Login failed';
      toast.error(message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (data: RegisterForm) => {
    try {
      setIsLoading(true);
      
      // Remove confirmPassword from the data sent to API
      const { confirmPassword, terms, ...registerData } = data;
      
      const response = await authApi.register(registerData);
      
      setUser(response.user);
      
      // Store token if provided
      if (response.token) {
        localStorage.setItem('auth_token', response.token);
      }
      
      toast.success('Welcome to Civic Transparency!');
    } catch (error: any) {
      const message = error.response?.data?.error || error.message || 'Registration failed';
      toast.error(message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      // Logout on client side even if server request fails
      console.error('Logout request failed:', error);
    } finally {
      setUser(null);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('csrf_token');
      toast.success('Logged out successfully');
    }
  };

  const updateProfile = async (updates: Partial<User>) => {
    try {
      const response = await authApi.updateProfile(updates);
      setUser(response.user);
      toast.success('Profile updated successfully');
    } catch (error: any) {
      const message = error.response?.data?.error || error.message || 'Profile update failed';
      toast.error(message);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    login,
    register,
    logout,
    updateProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): UseAuthReturn => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
